import {
  useMutation,
  type UseMutationOptions,
  type UseMutationResult,
} from "@tanstack/react-query";
import { toast } from "../components/ui/Toaster";

type ToastedOptions<TData, TError, TVars, TOnMutateResult> = UseMutationOptions<
  TData,
  TError,
  TVars,
  TOnMutateResult
> & {
  /**
   * Success toast text (or a function deriving it from the result). Pass
   * `false` or omit to skip the success toast.
   */
  success?: string | false | ((data: TData) => string | false);
  /**
   * Prefix for the error toast: `${errorPrefix}: ${extractedMessage}`. The
   * extracted message comes from `err.error`, `err.message`, or coerced
   * String(err).
   */
  errorPrefix?: string;
};

export function useMutationToast<
  TData = unknown,
  TError = unknown,
  TVars = void,
  TOnMutateResult = unknown,
>(
  options: ToastedOptions<TData, TError, TVars, TOnMutateResult>,
): UseMutationResult<TData, TError, TVars, TOnMutateResult> {
  const { success, errorPrefix, onSuccess, onError, ...rest } = options;
  return useMutation<TData, TError, TVars, TOnMutateResult>({
    ...rest,
    onSuccess: (data, vars, onMutateResult, ctx) => {
      if (success !== undefined && success !== false) {
        const msg = typeof success === "function" ? success(data) : success;
        if (msg) toast.success(msg);
      }
      return onSuccess?.(data, vars, onMutateResult, ctx);
    },
    onError: (err, vars, onMutateResult, ctx) => {
      const detail = extractMessage(err);
      toast.error(errorPrefix ? `${errorPrefix}: ${detail}` : detail);
      return onError?.(err, vars, onMutateResult, ctx);
    },
  });
}

function extractMessage(err: unknown): string {
  if (typeof err === "string") return err;
  if (err && typeof err === "object") {
    const e = err as Record<string, unknown>;
    if (typeof e.error === "string") return e.error;
    if (typeof e.message === "string") return e.message;
  }
  return "Something went wrong";
}
